import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Traps Tab/Shift+Tab within `containerRef` for as long as this hook is
 * mounted, and restores focus to whatever had it beforehand on unmount.
 *
 * Every dialog in this app already focuses its panel (or a specific field)
 * on mount and listens for Escape itself (`role="alertdialog"`/`"dialog"`,
 * `aria-modal="true"`) — Phase 10 found the one piece none of them had: Tab
 * could walk focus out of an open dialog into the page behind it, since
 * nothing cycled it back to the start. This hook adds only that.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Tab') return;
      const container = containerRef.current;
      if (container === null) return;

      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      const activeIsInside = active !== null && container.contains(active);

      if (event.shiftKey) {
        if (!activeIsInside || active === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (!activeIsInside || active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);

      // The trigger that opened the dialog is usually still in the document
      // (the dialog overlays it rather than replacing it) — restoring focus
      // there is what makes closing a dialog by keyboard not strand focus at
      // <body>.
      if (previouslyFocused !== null && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [containerRef]);
}
